import { FC, Fragment, useState } from "react"
import { Dialog, Transition } from "@headlessui/react"
import { AlertTriangle } from "lucide-react"

interface Props {
  open: boolean
  enrollmentId: number
  isDeleting: boolean
  onConfirm: () => void
  onCancel: () => void
}

const DeleteConfirmationModal: FC<Props> = ({
  open,
  enrollmentId,
  isDeleting,
  onConfirm,
  onCancel
}) => {
  const [confirmInput, setConfirmInput] = useState("")
  const isConfirmed = confirmInput === String(enrollmentId)

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => !isDeleting && onCancel()}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-xl bg-white p-6 shadow-xl transition-all">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-100 text-red-600">
                    <AlertTriangle size={20} />
                  </div>
                  <div>
                    <Dialog.Title className="text-base font-semibold text-neutral-900">
                      Delete enrollment #{enrollmentId}
                    </Dialog.Title>
                    <p className="mt-2 text-sm text-neutral-600">
                      This will permanently delete the enrollment and all
                      associated resources (org, brand, loan officer, rateflow,
                      profiles, workflow, embeds, billing config, and Stripe
                      customer). This cannot be undone.
                    </p>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Type{" "}
                    <span className="font-mono font-bold">{enrollmentId}</span>{" "}
                    to confirm
                  </label>
                  <input
                    type="text"
                    value={confirmInput}
                    onChange={e => setConfirmInput(e.target.value)}
                    placeholder={String(enrollmentId)}
                    disabled={isDeleting}
                    className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm font-mono focus:border-red-500 focus:ring-1 focus:ring-red-500 disabled:bg-neutral-50"
                  />
                </div>

                <div className="mt-5 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={onCancel}
                    disabled={isDeleting}
                    className="rounded-md px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-100 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={onConfirm}
                    disabled={!isConfirmed || isDeleting}
                    className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isDeleting ? "Deleting..." : "Delete enrollment"}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default DeleteConfirmationModal
